import {Person, TimeRegistrations, Week} from '../TimesheetsCalendarTypes';
import {useIntl} from 'react-intl';
import {isAfter, isBefore, isSameDay} from 'date-fns';
import {isBetween, isWeekSelected, onKeyboardEventHandler, workingHourForTheWeek} from '../TimesheetsCalendarUtil';
import {CellText, WeekCellStyle} from '../TimesheetsCalendar_styled';
import {FlexRow, Icon, IconTooltip} from '@forecast-it/design-system';
import React from 'react';
import Util from '../../../../shared/util/util';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';

type WeekCellProps = {
	week: Week;
	lockedDate: Date;
	timeRegistrations: TimeRegistrations;
	currentPersonViewed: Person;
	selectedDate: string;
	onSelect: () => void;
	hideWeekend: boolean;
};

const WeekCell = ({
	week,
	lockedDate,
	timeRegistrations,
	currentPersonViewed,
	selectedDate,
	onSelect,
	hideWeekend,
}: WeekCellProps): React.JSX.Element => {
	const intl = useIntl();
	const firstDayOfWeek = week.days[0];
	const lastDayOfWeek = hideWeekend ? week.days[4] : week.days[6]; // If weekend is hidden there is two fewer days in the weeks day array

	const holidays = currentPersonViewed.holidayCalendar?.holidayCalendarEntries?.edges.map(entry => {
		const {day, month, year} = entry.node;
		return new Date(year, month - 1, day);
	});

	const timeRegistrationsForWeek = timeRegistrations.edges.filter(timeReg => {
		const date = new Date(timeReg.node.year, timeReg.node.month - 1, timeReg.node.day);
		return isBetween(date, firstDayOfWeek, lastDayOfWeek);
	});
	const totalTime = timeRegistrationsForWeek.reduce(
		(totalTimeRegistered, timeReg) => totalTimeRegistered + timeReg.node.minutesRegistered,
		0
	);

	let workingHours = workingHourForTheWeek(currentPersonViewed, lastDayOfWeek);

	// Find holidays in the current week
	const holidaysInWeek = holidays?.filter(holiday => isBetween(holiday, firstDayOfWeek, lastDayOfWeek)) || [];

	// Subtract the correct working hours for each holiday
	holidaysInWeek.forEach(holiday => {
		const holidayWeekday = holiday.getDay();
		const dayKey = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][holidayWeekday];
		workingHours -= currentPersonViewed[dayKey] || 0;
	});

	const totalTimeInHoursFormatted = Util.convertMinutesToFullHour(totalTime, intl, true);
	const workingHoursFormatted = Util.convertMinutesToFullHour(workingHours, intl, true);
	const weekSelected = isWeekSelected(selectedDate, week);

	const showLock = lockedDate && (isSameDay(lockedDate, lastDayOfWeek) || isAfter(lockedDate, lastDayOfWeek));
	const showWeekFull = totalTime >= workingHours && workingHours > 0;
	const showCheckMark = !showLock && showWeekFull;
	const showWarning = !showLock && !showCheckMark && isBefore(lastDayOfWeek, new Date()) && workingHours > 0;

	return (
		<WeekCellStyle
			tabIndex={0}
			showWeekFull={showWeekFull}
			weekSelected={weekSelected}
			onClick={() => onSelect()}
			onKeyDown={event => onKeyboardEventHandler(event, () => onSelect())}
		>
			<FlexRow>
				<FlexRow flex={'0 0 auto'} justifyContent="flex-start">
					<CellText>
						{timeRegistrationsForWeek.length > 0 ? `${totalTimeInHoursFormatted} / ${workingHoursFormatted}` : null}
					</CellText>
				</FlexRow>
				<FlexRow flex={'1 0'} justifyContent="flex-start">
					{showLock ? (
						<IconTooltip icon={'lockClosed'}>The week is locked for new time registrations</IconTooltip>
					) : null}
					{showCheckMark ? <Icon color={'success'} icon="checkCircle" /> : null}
					{showWarning ? (
						<IconTooltip
							icon={'warning'}
							onOpenChange={(open: boolean) => open && trackEvent('Timesheet Week Warning Tooltip', 'Opened')}
						>
							Not enough time registered to fill your weekly timesheet
						</IconTooltip>
					) : null}
					{!showLock && !showCheckMark && !showWarning ? <div></div> : null}
				</FlexRow>
			</FlexRow>
		</WeekCellStyle>
	);
};

export default WeekCell;
